import { Box } from "@mui/material";
import type { UnitType } from "@sinch/core/entities/serverEnums.types";
import { MdsAdd } from "@sinch/icons";
import React, { useCallback, useState } from "react";
import { IntervalFormDialog } from "@sinch/components/application/RateInterval/IntervalFormDialog";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { MultilevelSelect } from "@sinch/components/form/MultilevelSelect/MultilevelSelect";
import { BasicOptions, NestedOptions, RenderedOptions } from "@sinch/components/form/MultilevelSelect/types";
import { IconTypography } from "@sinch/components/typography/IconTypography/IconTypography";

type MultiselectValue = {
  type: UnitType | null;
  customIntervalId: string | null;
} | null;
type IntervalOption = NestedOptions<
  MultiselectValue,
  BasicOptions<MultiselectValue> & { description: string | null | undefined }
>;

export const IntervalMultilevelSelect = ({
  onClose,
  options,
  value,
  onChange,
  onAddCustomInterval,
}: {
  value: MultiselectValue;
  onChange: (value: { type: UnitType | null; customIntervalId: string | null } | null) => void;
  onClose?: () => void;
  options: IntervalOption[];
  onAddCustomInterval: (id?: string) => void;
}) => {
  const { rateType } = useRateBuilderSettings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleOnCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleChange = (val: MultiselectValue) => {
    if (val?.customIntervalId === "__addCustomInterval__") {
      setDialogOpen(true);
    } else {
      onChange?.(val);
    }
    onClose?.();
  };

  const handleRenderItem = useCallback(
    ({ value, label }: Pick<RenderedOptions<MultiselectValue>, "value" | "label">) => {
      if (value?.customIntervalId === "__addCustomInterval__") {
        return (
          <IconTypography
            icon={MdsAdd}
            iconColor={(theme) => theme.palette.info.main}
            sx={{ color: (theme) => theme.palette.info.main, whiteSpace: "nowrap" }}
          >
            {label as string}
          </IconTypography>
        );
      }
      return null;
    },
    []
  );

  return (
    <>
      <Box sx={{ height: "400px" }}>
        <MultilevelSelect<MultiselectValue>
          data-cy="IntervalSelect-multilevelSelect"
          onChange={(_, val) => handleChange(val)}
          options={options}
          renderItem={handleRenderItem}
          value={value ?? null}
        />
      </Box>
      {dialogOpen && (
        <IntervalFormDialog
          onClose={handleOnCloseDialog}
          onSave={onAddCustomInterval}
          open={dialogOpen}
          ratelistType={rateType}
        />
      )}
    </>
  );
};
